import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Button } from "../Button";
import { parseEmailBody } from "../../utils/emailBodyParser";
import mailClientAPI from "../../api/mail-client-api";
import md5 from "md5";
import { Popover, Tooltip, Spin, Skeleton, notification } from "antd";
import { LoadingOutlined } from '@ant-design/icons';
import { ReactMailQuill } from "./ReactMailQuill";
import classNames from "classnames";

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Helper function for gravatar
const getGravatarUrl = (email, size = 20) => {
  if (!email) return null;
  const emailHash = md5(email.toLowerCase().trim());
  return `https://www.gravatar.com/avatar/${emailHash}?s=${size}&d=404`;
};

// Email Chip Component
const EmailChip = ({ email, onRemove }) => {
  const [gravatarError, setGravatarError] = useState(false);
  const gravatarUrl = getGravatarUrl(email, 20);
  const initials = email ? email.charAt(0).toUpperCase() : "?";

  return (
    <div className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 rounded-full p-[2px] text-xs font-medium border border-gray-300">
      <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center overflow-hidden flex-shrink-0">
        {gravatarUrl && !gravatarError ? (
          <img
            src={gravatarUrl}
            alt={email}
            className="w-full h-full object-cover"
            onError={() => setGravatarError(true)}
          />
        ) : (
          <span className="material-icons-outlined text-xs text-blue-600">person</span>
        )}
      </div>
      <span>{email}</span>
      <button
        type="button"
        onClick={onRemove}
        className="flex ml-0.5 hover:bg-gray-200 rounded-full p-0.5 transition-colors flex items-center justify-center"
        aria-label={`Remove ${email}`}
      >
        <span className="material-icons-outlined text-sm text-gray-500">close</span>
      </button>
    </div>
  );
};

// Email Input Component with Chips
const EmailInput = ({ 
  emails, 
  setEmails, 
  placeholder, 
  maxEmails = Infinity,
  showCount = false,
  inputRef = null
}) => {
  const [inputValue, setInputValue] = useState("");

  const addEmail = (email) => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !EMAIL_REGEX.test(trimmedEmail)) {
      return false;
    }
    
    // Check for duplicates (case-insensitive)
    const isDuplicate = emails.some((e) => e.email.toLowerCase() === trimmedEmail.toLowerCase());
    if (isDuplicate) {
      return false;
    }

    // Check max emails limit
    if (emails.length >= maxEmails) {
      return false;
    }

    setEmails([...emails, { id: Date.now() + Math.random(), email: trimmedEmail }]);
    return true;
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    // Check for comma to create chip
    if (value.includes(",")) {
      const parts = value.split(",");
      const emailToAdd = parts[0].trim();
      if (emailToAdd) {
        if (addEmail(emailToAdd)) {
          // Email added successfully, process remaining parts
          const remaining = parts.slice(1).join(",").trim();
          setInputValue(remaining);
          // If there are more parts, recursively process them
          if (remaining.includes(",")) {
            setTimeout(() => {
              const event = { target: { value: remaining } };
              handleInputChange(event);
            }, 0);
          }
        } else {
          // Invalid or duplicate email, keep it in input
          setInputValue(value);
        }
      } else {
        // Empty before comma, just remove the comma
        setInputValue(parts.slice(1).join(",").trim());
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmedValue = inputValue.trim();
      if (trimmedValue) {
        if (addEmail(trimmedValue)) {
          setInputValue("");
        }
      }
    } else if (e.key === "Backspace" && inputValue === "" && emails.length > 0) {
      // Remove last chip on backspace when input is empty
      setEmails(emails.slice(0, -1));
    }
  };

  const handleBlur = () => {
    const trimmedValue = inputValue.trim();
    if (trimmedValue) {
      // Try to add the email if it's valid
      if (addEmail(trimmedValue)) {
        setInputValue("");
      } else {
        // If invalid or duplicate, clear the input on blur
        setInputValue("");
      }
    }
  };

  const removeEmail = (id) => {
    setEmails(emails.filter((e) => e.id !== id));
  };

  return (
    <div className="flex-1 flex items-center flex-wrap gap-1.5 min-h-[32px] py-1">
      {emails.map((emailObj) => (
        <EmailChip
          key={emailObj.id}
          email={emailObj.email}
          onRemove={() => removeEmail(emailObj.id)}
        />
      ))}
      <input
        ref={inputRef}
        type="text"
        className="flex-1 min-w-[120px] outline-none text-sm py-1 bg-transparent"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={emails.length === 0 ? placeholder : ""}
      />
      {showCount && emails.length > 0 && (
        <span className="text-xs text-gray-400 ml-2 whitespace-nowrap">
          {emails.length}/{maxEmails}
        </span>
      )}
    </div>
  );
};

// Component to display email body
const EmailBodyDisplay = ({ body, imagesAllowed = false, onImageClick }) => {
  const containerRef = useRef(null);
  const parsedBody = useMemo(() => {
    if (body) {
      return parseEmailBody(body, { preferHtml: true });
    }
    return { html: "", preview: "", isHtml: false };
  }, [body]);

  const sanitizedHtml = useMemo(() => {
    let html = parsedBody.html;
    
    // Make all links open in new tab
    html = html.replace(/<a([^>]*?)>/gi, (match, attrs) => {
      // Check if target already exists
      if (attrs.includes('target=')) {
        // Replace existing target
        attrs = attrs.replace(/target\s*=\s*["'][^"']*["']/gi, 'target="_blank"');
      } else {
        // Add target attribute
        attrs += ' target="_blank"';
      }
      
      // Check if rel already exists
      if (attrs.includes('rel=')) {
        // Add noopener noreferrer to existing rel if not present
        if (!attrs.includes('noopener') && !attrs.includes('noreferrer')) {
          attrs = attrs.replace(/rel\s*=\s*["']([^"']*?)["']/gi, (relMatch, relValue) => {
            return `rel="${relValue} noopener noreferrer"`;
          });
        }
      } else {
        // Add rel attribute
        attrs += ' rel="noopener noreferrer"';
      }
      
      return `<a${attrs}>`;
    });
    
    if (!parsedBody.isHtml || imagesAllowed) {
      return html;
    }
    
    // Block images by replacing src with placeholder image
    const placeholderImagePath = "/images/hidden-image-placeholder.avif";
    
    // Replace img tags to show placeholder instead of original image
    html = html.replace(/<img([^>]*?)src\s*=\s*["']([^"']*?)["']([^>]*?)>/gi, (match, before, src, after) => {
      // Replace src with placeholder and store original src, set size to 150x150
      // Add cursor pointer style and data attribute for click detection
      return `<img${before} src="${placeholderImagePath}" width="150" height="150"${after} data-blocked-src="${src}" style="cursor: pointer;" />`;
    });
    
    // Also handle background images in style attributes
    html = html.replace(/style\s*=\s*["']([^"']*?)background-image\s*:\s*url\([^)]+\)([^"']*?)["']/gi, (match, before, after) => {
      return `style="${before}${after}"`;
    });
    
    return html;
  }, [parsedBody.html, parsedBody.isHtml, imagesAllowed]);

  // Add click handlers to blocked images
  useEffect(() => {
    if (!imagesAllowed && containerRef.current && onImageClick) {
      const handleImageClick = (e) => {
        // Check if clicked element is a blocked image or inside one
        const img = e.target.tagName === 'IMG' ? e.target : e.target.closest('img');
        if (img && img.hasAttribute('data-blocked-src')) {
          e.preventDefault();
          e.stopPropagation();
          onImageClick();
        }
      };
      
      containerRef.current.addEventListener('click', handleImageClick);
      return () => {
        if (containerRef.current) {
          containerRef.current.removeEventListener('click', handleImageClick);
        }
      };
    }
  }, [imagesAllowed, onImageClick, sanitizedHtml]);

  if (parsedBody.isHtml) {
    return (
      <div className="email-body-wrapper">
        <style>{`
          .email-body-wrapper {
            width: 100%;
            padding: 20px 0px;
            background-color: #ffffff;
            border-radius: 4px;
            margin: 0 auto;
          }
          
          @media (max-width: 768px) {
            .email-body-wrapper {
              max-width: 100%;
              padding: 16px 20px;
            }
          }
          
          .email-body-content {
            max-width: 100%;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            font-size: 14px;
            line-height: 1.6;
            color: #202124;
            word-wrap: break-word;
            overflow-wrap: break-word;
          }
          
          .email-body-content * {
            max-width: 100%;
            box-sizing: border-box;
          }
          
          /* Reset margins and padding for email content */
          .email-body-content p {
            margin: 0 0 12px 0;
            padding: 0;
          }
          
          .email-body-content p:last-child {
            margin-bottom: 0;
          }
          
          .email-body-content h1,
          .email-body-content h2,
          .email-body-content h3,
          .email-body-content h4,
          .email-body-content h5,
          .email-body-content h6 {
            margin: 16px 0 8px 0;
            padding: 0;
            font-weight: 600;
            line-height: 1.4;
          }
          
          .email-body-content h1:first-child,
          .email-body-content h2:first-child,
          .email-body-content h3:first-child,
          .email-body-content h4:first-child,
          .email-body-content h5:first-child,
          .email-body-content h6:first-child {
            margin-top: 0;
          }
          
          .email-body-content h1 { font-size: 24px; }
          .email-body-content h2 { font-size: 20px; }
          .email-body-content h3 { font-size: 18px; }
          .email-body-content h4 { font-size: 16px; }
          .email-body-content h5 { font-size: 14px; }
          .email-body-content h6 { font-size: 12px; }
          
          .email-body-content ul,
          .email-body-content ol {
            margin: 12px 0;
            padding-left: 24px;
          }
            
          .email-body-content ul {
            list-style: disc;
          }

          .email-body-content ol {
            list-style: decimal;
          }
          
          .email-body-content li {
            margin: 4px 0;
            padding: 0;
          }
          
          .email-body-content blockquote {
            margin: 12px 0;
            padding: 8px 16px;
            border-left: 3px solid #dadce0;
            background-color: #f8f9fa;
            color: #5f6368;
          }
          
          .email-body-content pre {
            margin: 12px 0;
            padding: 12px;
            background-color: #f8f9fa;
            border: 1px solid #dadce0;
            border-radius: 4px;
            overflow-x: auto;
            font-family: 'Courier New', Courier, monospace;
            font-size: 13px;
            line-height: 1.5;
          }
          
          .email-body-content code {
            background-color: #f8f9fa;
            padding: 2px 4px;
            border-radius: 3px;
            font-family: 'Courier New', Courier, monospace;
            font-size: 13px;
          }
          
          .email-body-content pre code {
            background-color: transparent;
            padding: 0;
          }
          
          .email-body-content a {
            color: #1a73e8;
            text-decoration: none;
          }
          
          .email-body-content a:hover {
            text-decoration: underline;
          }
          
          .email-body-content img {
            max-width: 100%;
          }
          
          .email-body-content table {
          }
          
          .email-body-content table td,
          .email-body-content table th {
          }
          
          .email-body-content table th {
            background-color: #f8f9fa;
            font-weight: 600;
          }
          
          .email-body-content hr {
            border: none;
            border-top: 1px solid #dadce0;
            margin: 16px 0;
          }
          
          .email-body-content strong,
          .email-body-content b {
            font-weight: 600;
          }
          
          .email-body-content em,
          .email-body-content i {
            font-style: italic;
          }
          
          .email-body-content u {
            text-decoration: underline;
          }
          
          /* Ensure text doesn't overflow */
          .email-body-content {
            overflow-wrap: break-word;
            word-break: break-word;
          }
        `}</style>
        <div
          ref={containerRef}
          className="email-body-content"
          dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
        />
      </div>
    );
  } else {
    return (
      <div className="email-body-wrapper">
        <style>{`
          .email-body-wrapper {
            width: 100%;
            padding: 20px 24px;
            background-color: #ffffff;
            border-radius: 4px;
            margin: 0 auto;
          }
          
          @media (max-width: 768px) {
            .email-body-wrapper {
              max-width: 100%;
              padding: 16px 20px;
            }
          }
          
          .email-body-content {
            max-width: 100%;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            font-size: 14px;
            line-height: 1.6;
            color: #202124;
            word-wrap: break-word;
            overflow-wrap: break-word;
            white-space: pre-wrap;
          }
        `}</style>
        <div className="email-body-content">
          {parsedBody.html.replace(/<br\s*\/?>/gi, "\n")}
        </div>
      </div>
    );
  }
};

export const EmailDetail = ({ email, onBack, onFlagChange, mailboxPath, onArchive, onSpam, onDelete, onMarkAsUnread, onStar, imagesAllowed = false, showTrustBar = false, onAllowImages, onAlwaysAllowImages, onReply, userInfo, labels = [], labelsLoading = false, refreshLabels, onEmailUpdate }) => {
  const [replyMode, setReplyMode] = useState(null); // "reply", "replyAll", or "forward"
  const [replyBody, setReplyBody] = useState("");
  const [replyToEmails, setReplyToEmails] = useState([]);
  const [replyCcEmails, setReplyCcEmails] = useState([]);
  const [replyBccEmails, setReplyBccEmails] = useState([]);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [replySubject, setReplySubject] = useState("");
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [sendingReply, setSendingReply] = useState(false);
  const [showFormattingToolbar, setShowFormattingToolbar] = useState(false);
  const [trustBarHighlighted, setTrustBarHighlighted] = useState(false);
  const [gravatarError, setGravatarError] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [downloadingAttachments, setDownloadingAttachments] = useState(new Set());
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  const [replySuggestions, setReplySuggestions] = useState(null);
  const [replySuggestionsLoading, setReplySuggestionsLoading] = useState(false);
  const [replySuggestionsError, setReplySuggestionsError] = useState(null);
  const [labelPopoverOpen, setLabelPopoverOpen] = useState(false);
  const [emailLabels, setEmailLabels] = useState([]);
  const [aiToolsOpen, setAiToolsOpen] = useState(false);
  const [isRefining, setIsRefining] = useState(false);
  const pendingAiReplyRef = useRef(null);
  const trustBarRef = useRef(null);
  const fileInputRef = useRef(null);
  const aiToolsRef = useRef(null);
  const quillRef = useRef(null);
  const manuallyUpdatedLabelsRef = useRef(false);
  const { emailId } = useParams();

  // Quill modules configuration for custom toolbar
  const modules = useMemo(() => {
    return {
      toolbar: {
        container: "#rich-area-text-effect-reply",
      },
    };
  }, []);

  // Parse sender name and email from formats like "Pratik Kinage" <pratikkinage@gmail.com>
  const parseSender = (senderName, senderEmail) => {
    // If senderName contains email format like "Name <email@domain.com>"
    if (senderName && senderName.includes('<') && senderName.includes('>')) {
      const match = senderName.match(/^(.+?)\s*<(.+?)>$/);
      if (match) {
        // Remove quotes from name
        let name = match[1].trim();
        name = name.replace(/^["']|["']$/g, '');
        return {
          name: name,
          email: match[2].trim()
        };
      }
    }
    // If senderName is just an email
    if (senderName && senderName.includes('@') && !senderEmail) {
      return {
        name: null,
        email: senderName
      };
    }
    // Default: use provided values, remove quotes
    let name = senderName || null;
    if (name) {
      name = name.replace(/^["']|["']$/g, '');
    }
    return {
      name: name,
      email: senderEmail || null
    };
  };

  // Generate Gravatar URL from email
  const getGravatarUrl = (email, size = 40) => {
    if (!email) return null;
    const emailHash = md5(email.toLowerCase().trim());
    return `https://www.gravatar.com/avatar/${emailHash}?s=${size}&d=404`;
  };

  // Handle image click to highlight trust bar
  const handleImageClick = () => {
    if (showTrustBar && trustBarRef.current) {
      setTrustBarHighlighted(true);
      trustBarRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      // Remove highlight after animation
      setTimeout(() => setTrustBarHighlighted(false), 2000);
    }
  };

  // Parse sender information
  const parsedSender = parseSender(email.senderName, email.senderEmail);
  const senderEmail = parsedSender.email;
  const senderName = parsedSender.name;
  const gravatarUrl = senderEmail ? getGravatarUrl(senderEmail, 40) : null;
  const initials = (senderName || senderEmail || "U").charAt(0).toUpperCase();

  // Reset gravatar error when email changes
  useEffect(() => {
    setGravatarError(false);
  }, [email?.id, senderEmail]);

  // Reset summary when email changes, or set it if email already has a cached summary
  useEffect(() => {
    if (email?.summary) {
      // Email has a cached summary, use it
      setSummary(email.summary);
      setSummaryError(null);
      setSummaryLoading(false);
    } else {
      // No cached summary, reset state
      setSummary(null);
      setSummaryError(null);
      setSummaryLoading(false);
    }
  }, [email?.id, emailId, email?.summary]);

  // Reset reply suggestions when email changes, or set them if email already has cached suggestions
  useEffect(() => {
    if (email?.replySuggestions) {
      // Email has cached reply suggestions, use them
      setReplySuggestions(email.replySuggestions);
      setReplySuggestionsError(null);
      setReplySuggestionsLoading(false);
    } else {
      // No cached suggestions, reset state
      setReplySuggestions(null);
      setReplySuggestionsError(null);
      setReplySuggestionsLoading(false);
    }
  }, [email?.id, emailId, email?.replySuggestions]);

  // Set user email from userInfo
  useEffect(() => {
    if (userInfo?.email) {
      setUserEmail(userInfo.email);
    }
  }, [userInfo]);

  // Update email labels when email changes, filtering out labels that don't exist
  useEffect(() => {
    if (email?.labels) {
      // Filter out labels that don't exist in the available labels list
      const validLabels = email.labels.filter(label => 
        labels.some(availableLabel => availableLabel.id === label.id)
      );
      setEmailLabels(validLabels);
      manuallyUpdatedLabelsRef.current = false; // Reset flag when email changes
    } else {
      setEmailLabels([]);
      manuallyUpdatedLabelsRef.current = false;
    }
  }, [email?.labels, email?.id, labels]);

  // Filter out deleted labels when labels context changes
  // This ensures deleted labels are removed from the UI
  useEffect(() => {
    if (emailLabels.length > 0) {
      const validLabels = emailLabels.filter(label => 
        labels.some(availableLabel => availableLabel.id === label.id)
      );
      if (validLabels.length !== emailLabels.length) {
        setEmailLabels(validLabels);
        // Update email object if callback is provided
        if (onEmailUpdate && email) {
          onEmailUpdate({ ...email, labels: validLabels });
        }
      }
    }
  }, [labels]); // Only run when labels context changes

  // Initialize reply when replyMode changes
  useEffect(() => {
    if (replyMode && email) {
      const parsedSender = parseSender(email.senderName, email.senderEmail);
      const senderEmail = parsedSender.email;
      
      if (replyMode === "reply") {
        // Reply: To = original sender
        setReplyToEmails(senderEmail ? [{ id: Date.now(), email: senderEmail }] : []);
        setReplyCcEmails([]);
        setReplyBccEmails([]);
        setShowCcBcc(false);
        setReplySubject(email.subject?.startsWith("Re: ") ? email.subject : `Re: ${email.subject || "(No Subject)"}`);
      } else if (replyMode === "replyAll") {
        // Reply All: To = original sender, CC = original CC + original To (excluding user)
        setReplyToEmails(senderEmail ? [{ id: Date.now(), email: senderEmail }] : []);
        const originalTo = email.to || [];
        const originalCc = email.cc || [];
        const allRecipients = [...originalTo, ...originalCc]
          .map(addr => typeof addr === 'string' ? addr : (addr.email || addr))
          .filter(addrEmail => addrEmail && addrEmail.toLowerCase() !== userEmail?.toLowerCase())
          .map((addrEmail, idx) => ({ id: Date.now() + idx, email: addrEmail }));
        setReplyCcEmails(allRecipients);
        setReplyBccEmails([]);
        setShowCcBcc(allRecipients.length > 0);
        setReplySubject(email.subject?.startsWith("Re: ") ? email.subject : `Re: ${email.subject || "(No Subject)"}`);
      } else if (replyMode === "forward") {
        // Forward: To = empty, Subject = Fwd: original subject
        setReplyToEmails([]);
        setReplyCcEmails([]);
        setReplyBccEmails([]);
        setShowCcBcc(false);
        setReplySubject(email.subject?.startsWith("Fwd: ") ? email.subject : `Fwd: ${email.subject || "(No Subject)"}`);
      }

      // Check if there's a pending AI-generated reply
      if (pendingAiReplyRef.current) {
        setReplyBody(pendingAiReplyRef.current);
        pendingAiReplyRef.current = null;
      } else if (!replyBody || replyBody.trim() === "") {
        // Create quoted body only if replyBody is empty
        const originalBody = email.body || email.preview || "";
        const quotedBody = replyMode === "forward" 
          ? `<br><br>---------- Forwarded message ----------<br>From: ${email.senderName || email.senderEmail || ""}<br>Date: ${formatDate(email.timestamp)}<br>Subject: ${email.subject || "(No Subject)"}<br>To: ${(email.to || []).join(", ")}<br><br>${originalBody}`
          : `<br><br><div style="border-left: 3px solid #ccc; padding-left: 10px; margin-left: 10px; color: #666;"><br>On ${formatDate(email.timestamp)}, ${email.senderName || email.senderEmail || ""} wrote:<br><br>${originalBody}</div>`;
        
        setReplyBody(quotedBody);
      }
      setReplyAttachments([]);
    }
  }, [replyMode, email, userEmail]);


  const isArchiveFolder = mailboxPath && mailboxPath.toLowerCase().includes("archive");
  
  // Format recipient string - show "me" if it's the user's email
  const formatRecipient = (recipients) => {
    if (!recipients || recipients.length === 0) return "me";
    return recipients.map(recipient => {
      const recipientEmail = typeof recipient === 'string' ? recipient : recipient.email || recipient;
      return recipientEmail.toLowerCase() === userEmail?.toLowerCase() ? "me" : recipientEmail;
    }).join(", ");
  };

  // Format recipient string for popover - always show actual email addresses
  const formatRecipientForPopover = (recipients) => {
    if (!recipients || recipients.length === 0) return "N/A";
    return recipients.map(recipient => {
      const recipientEmail = typeof recipient === 'string' ? recipient : recipient.email || recipient;
      return recipientEmail;
    }).join(", ");
  };
  
  const recipientString = formatRecipient(email.to);

  const formatDate = (date) => {
    if (!date) return "";
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
  };

  const handleSendReply = async () => {
    if (!onReply) {
      notification.error({
        message: "Reply handler not available",
        duration: 3,
      });
      return;
    }

    if (replyToEmails.length === 0 && replyMode !== "forward") {
      notification.error({
        message: "Please enter at least one recipient",
        duration: 3,
      });
      return;
    }

    setSendingReply(true);
    try {
      const to = replyToEmails.map(e => e.email).join(",");
      const cc = replyCcEmails.length > 0 ? replyCcEmails.map(e => e.email).join(",") : undefined;
      const bcc = replyBccEmails.length > 0 ? replyBccEmails.map(e => e.email).join(",") : undefined;

      await onReply({
        to,
        cc,
        bcc,
        subject: replySubject,
        body: replyBody,
        attachments: replyAttachments.map(att => att.file),
      });

      // Reset reply state
      setReplyMode(null);
      setReplyBody("");
      setReplyToEmails([]);
      setReplyCcEmails([]);
      setReplyBccEmails([]);
      setShowCcBcc(false);
      setReplySubject("");
      setReplyAttachments([]);
    } catch (error) {
      // Error is already handled in onReply
      console.error("Error sending reply:", error);
    } finally {
      setSendingReply(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const newAttachments = files.map((file) => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
    }));
    setReplyAttachments([...replyAttachments, ...newAttachments]);
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeAttachment = (id) => {
    setReplyAttachments(replyAttachments.filter((att) => att.id !== id));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  // Handle rewrite/refine email
  const handleRewrite = async (rewriteType) => {
    if (!replyBody) return;
    
    setIsRefining(true);
    try {
      // Extract plain text from HTML if needed (ReactQuill uses HTML)
      // Create a temporary div to extract text content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = replyBody;
      const plainText = tempDiv.textContent || tempDiv.innerText || replyBody.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
      
      if (!plainText || plainText.trim().length === 0) {
        notification.warning({
          message: "No text to rewrite",
          description: "Please add some text to your email before rewriting.",
          placement: "bottomLeft",
        });
        setIsRefining(false);
        return;
      }
      
      const response = await mailClientAPI.rewriteEmail(plainText, rewriteType);
      if (response.Output?.rewrittenText) {
        // ReactQuill expects HTML, so we'll set the plain text and let ReactQuill handle it
        // The rewritten text will be displayed as-is (ReactQuill will convert it to HTML)
        setReplyBody(response.Output.rewrittenText);
        setAiToolsOpen(false);
        notification.success({
          message: "Email rewritten",
          placement: "bottomLeft",
        });
      } else {
        throw new Error("No rewritten text received");
      }
    } catch (error) {
      console.error("Error rewriting email:", error);
      notification.error({
        message: "Failed to rewrite email",
        description: error.response?.data?.Output?.message || "Please try again.",
        placement: "bottomLeft",
      });
    } finally {
      setIsRefining(false);
    }
  };

  // Handle click outside for AI tools dropdown
  useEffect(() => {
    if (!replyMode) return; // Only active in reply mode
    
    const handleClickOutside = (event) => {
      const target = event.target;
      
      // Check if click is outside AI tools dropdown
      if (aiToolsOpen && aiToolsRef.current && !aiToolsRef.current.contains(target)) {
        setAiToolsOpen(false);
      }
    };

    if (aiToolsOpen) {
      // Attach listener on next tick to avoid interfering with current click
      const timeoutId = setTimeout(() => {
        document.addEventListener("click", handleClickOutside);
      }, 0);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener("click", handleClickOutside);
      };
    }
  }, [replyMode, aiToolsOpen]);

  if (!email) return null;


  return (
    <div className="flex flex-col h-full bg-white">
      {/* Toolbar */}
      <div className="flex items-center px-4 py-2 border-b border-gray-100 bg-white sticky top-0 z-10">
        <Tooltip title="Back" mouseEnterDelay={1}>
          <button onClick={onBack} className="flex items-center justify-center mr-4 p-2 hover:bg-gray-100 rounded-full text-gray-500">
            <span className="material-icons-outlined">arrow_back</span>
          </button>
        </Tooltip>

        <div className="flex space-x-1">
          {mailboxPath !== "Scheduled" && (
            <Tooltip title="Archive" mouseEnterDelay={1}>
              <button 
                className="flex items-center justify-center p-2 text-gray-500 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed" 
                disabled={isArchiveFolder}
                onClick={onArchive}
              >
                <span className="material-icons-outlined">archive</span>
              </button>
            </Tooltip>
          )}
          {mailboxPath !== "Scheduled" && (
            <Tooltip title="Report Spam" mouseEnterDelay={1}>
              <button 
                className="flex items-center justify-center p-2 text-gray-500 hover:bg-gray-100 rounded" 
                onClick={onSpam}
              >
                <span className="material-icons-outlined">report</span>
              </button>
            </Tooltip>
          )}
          <Tooltip title="Delete" mouseEnterDelay={1}>
            <button 
              className="flex items-center justify-center p-2 text-gray-500 hover:bg-gray-100 rounded" 
              onClick={onDelete}
            >
              <span className="material-icons-outlined">delete</span>
            </button>
          </Tooltip>
        </div>

        <div className="flex-1"></div>

        <div className="flex space-x-1">
          {mailboxPath !== "Scheduled" && (
            <>
              {email.isRead ? (
                <Tooltip title="Mark as Unread" mouseEnterDelay={1}>
                  <button
                    className="flex items-center justify-center p-2 text-gray-500 hover:bg-gray-100 rounded"
                    onClick={onMarkAsUnread}
                  >
                    <span className="material-icons-outlined">mark_email_unread</span>
                  </button>
                </Tooltip>
              ) : (
                <Tooltip title="Mark as Read" mouseEnterDelay={1}>
                  <button
                    className="flex items-center justify-center p-2 text-gray-500 hover:bg-gray-100 rounded"
                    onClick={() => {
                      if (onFlagChange) {
                        onFlagChange(["\\Seen"], []);
                      }
                    }}
                  >
                    <span className="material-icons-outlined">mark_email_read</span>
                  </button>
                </Tooltip>
              )}
              <Tooltip title={email.isStarred ? "Unstar" : "Star"} mouseEnterDelay={1}>
                <button
                  className={`flex items-center justify-center p-2 rounded ${email.isStarred ? "text-yellow-400" : "text-gray-500"} hover:bg-gray-100`}
                  onClick={onStar}
                >
                  <span className="material-icons-outlined">{email.isStarred ? "star" : "star_border"}</span>
                </button>
              </Tooltip>
            </>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Header Section */}
        <div className="mb-8 border-b border-gray-100 pb-6">
          <div className="flex justify-between items-start mb-6 gap-4">
            <h1 className="text-2xl font-bold text-gray-900 leading-tight flex-1">
              {email.subject || "(No Subject)"}
            </h1>
            {mailboxPath !== "Scheduled" && (
              <div className="flex items-center space-x-1">
                <Tooltip title="Reply" mouseEnterDelay={1}>
                  <button
                    className="flex items-center justify-center p-2 text-gray-500 hover:bg-gray-100 hover:text-grigora-600 rounded-lg transition-colors"
                    onClick={() => setReplyMode("reply")}
                  >
                    <span className="material-icons-outlined">reply</span>
                  </button>
                </Tooltip>
                <Tooltip title="Reply All" mouseEnterDelay={1}>
                  <button
                    className="flex items-center justify-center p-2 text-gray-500 hover:bg-gray-100 hover:text-grigora-600 rounded-lg transition-colors"
                    onClick={() => setReplyMode("replyAll")}
                  >
                    <span className="material-icons-outlined">reply_all</span>
                  </button>
                </Tooltip>
                <Tooltip title="Forward" mouseEnterDelay={1}>
                  <button
                    className="flex items-center justify-center p-2 text-gray-500 hover:bg-gray-100 hover:text-grigora-600 rounded-lg transition-colors"
                    onClick={() => setReplyMode("forward")}
                  >
                    <span className="material-icons-outlined">forward</span>
                  </button>
                </Tooltip>
              </div>
            )}
          </div>

          <div className="flex items-start justify-between relative">
            <div className="flex items-start flex-1">
              <div className="h-10 w-10 rounded-full bg-grigora-100 flex items-center justify-center text-grigora-600 font-bold text-lg mr-3 mt-1 overflow-hidden flex-shrink-0">
                {gravatarUrl && !gravatarError ? (
                  <img
                    src={gravatarUrl}
                    alt={senderName || senderEmail || "Sender"}
                    className="w-full h-full object-cover"
                    onError={() => setGravatarError(true)}
                  />
                ) : (
                  initials
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-900">
                    {senderName || senderEmail || "Unknown"}
                  </span>
                  {senderEmail && senderName && (
                    <span className="text-sm text-gray-500">&lt;{senderEmail}&gt;</span>
                  )}
                </div>
                <div className="flex items-center gap-1 mt-0.5">
                  <span className="text-xs text-gray-500">to {recipientString}</span>
                  <Popover
                    content={
                      <div className="space-y-2 text-sm min-w-[400px]">
                        <div className="flex">
                          <span className="font-medium text-gray-700 w-24 flex-shrink-0">From:</span>
                          <span className="text-gray-900">{senderName || senderEmail || "Unknown"}{senderEmail && senderName ? ` <${senderEmail}>` : ''}</span>
                        </div>
                        <div className="flex">
                          <span className="font-medium text-gray-700 w-24 flex-shrink-0">Reply-to:</span>
                          <span className="text-gray-900">{email.replyTo || senderEmail || "N/A"}</span>
                        </div>
                        <div className="flex">
                          <span className="font-medium text-gray-700 w-24 flex-shrink-0">To:</span>
                          <span className="text-gray-900">{formatRecipientForPopover(email.to)}</span>
                        </div>
                        {email.cc && email.cc.length > 0 && (
                          <div className="flex">
                            <span className="font-medium text-gray-700 w-24 flex-shrink-0">Cc:</span>
                            <span className="text-gray-900">{formatRecipientForPopover(email.cc)}</span>
                          </div>
                        )}
                        {email.bcc && email.bcc.length > 0 && (
                          <div className="flex">
                            <span className="font-medium text-gray-700 w-24 flex-shrink-0">Bcc:</span>
                            <span className="text-gray-900">{formatRecipientForPopover(email.bcc)}</span>
                          </div>
                        )}
                        <div className="flex">
                          <span className="font-medium text-gray-700 w-24 flex-shrink-0">Date:</span>
                          <span className="text-gray-900">{formatDate(email.timestamp || email.date)}</span>
                        </div>
                        {email.mailedBy && (
                          <div className="flex">
                            <span className="font-medium text-gray-700 w-24 flex-shrink-0">Mailed-by:</span>
                            <span className="text-gray-900">{email.mailedBy}</span>
                          </div>
                        )}
                        {email.signedBy && (
                          <div className="flex">
                            <span className="font-medium text-gray-700 w-24 flex-shrink-0">Signed-by:</span>
                            <span className="text-gray-900">{email.signedBy}</span>
                          </div>
                        )}
                      </div>
                    }
                    trigger="click"
                    placement="bottomLeft"
                    overlayInnerStyle={{ padding: '16px' }}
                    overlayStyle={{ padding: 0 }}
                    arrow={false}
                  >
                    <Tooltip title="Show details" mouseEnterDelay={1}>
                      <button
                        className="flex text-gray-400 hover:text-gray-600 transition-colors p-0.5"
                      >
                        <span className="material-icons-outlined text-sm">
                          arrow_drop_down
                        </span>
                      </button>
                    </Tooltip>
                  </Popover>
                </div>
              </div>
            </div>

            <div className="text-sm text-gray-500 mt-1">{formatDate(email.timestamp || email.date)}</div>
          </div>

          {/* Labels Row */}
          {mailboxPath !== "Scheduled" && (
            <div className="mt-4 flex items-center flex-wrap gap-2 pl-[52px]">
              {emailLabels.map((label) => (
                <span
                  key={label.id}
                  className="h-[20px] inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 group hover:bg-gray-200 cursor-pointer transition-colors"
                  style={{ backgroundColor: `${label.color}20`, color: label.color }}
                >
                  <span
                    className="w-2 h-2 rounded-full mr-1.5"
                    style={{ backgroundColor: label.color }}
                  ></span>
                  {label.name}
                  <Tooltip title="Remove label" mouseEnterDelay={1}>
                    <button
                      className="flex items-center justify-center ml-1.5 text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 leading-none"
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await mailClientAPI.applyLabelsToEmail(mailboxPath, emailId, [], [label.id]);
                          const updatedLabels = emailLabels.filter(l => l.id !== label.id);
                          manuallyUpdatedLabelsRef.current = true; // Mark as manually updated
                          setEmailLabels(updatedLabels);
                          // Update email object if callback is provided
                          if (onEmailUpdate && email) {
                            onEmailUpdate({ ...email, labels: updatedLabels });
                          }
                          if (refreshLabels) {
                            await refreshLabels();
                          }
                          notification.success({
                            message: "Label removed",
                            placement: "bottomLeft",
                          });
                        } catch (error) {
                          console.error("Error removing label:", error);
                          notification.error({
                            message: error.response?.data?.Output?.message || "Failed to remove label",
                            placement: "bottomLeft",
                          });
                        }
                      }}
                    >
                      ×
                    </button>
                  </Tooltip>
                </span>
              ))}
              <Popover
                content={
                  <div className="min-w-[200px] max-h-[300px] overflow-y-auto">
                    {labelsLoading ? (
                      <div className="p-4 text-center text-sm text-gray-500">Loading labels...</div>
                    ) : labels.length === 0 ? (
                      <div className="p-4 text-center text-sm text-gray-500">No labels available</div>
                    ) : (
                      <div className="py-1">
                        {labels.map((label) => {
                          const isApplied = emailLabels.some(l => l.id === label.id);
                          return (
                            <button
                              key={label.id}
                              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 ${
                                isApplied ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                              }`}
                              onClick={async () => {
                                if (isApplied) return;
                                try {
                                  await mailClientAPI.applyLabelsToEmail(mailboxPath, emailId, [label.id], []);
                                  const updatedLabels = [...emailLabels, label];
                                  manuallyUpdatedLabelsRef.current = true; // Mark as manually updated
                                  setEmailLabels(updatedLabels);
                                  setLabelPopoverOpen(false);
                                  // Update email object if callback is provided
                                  if (onEmailUpdate && email) {
                                    onEmailUpdate({ ...email, labels: updatedLabels });
                                  }
                                  if (refreshLabels) {
                                    await refreshLabels();
                                  }
                                  notification.success({
                                    message: "Label added",
                                    placement: "bottomLeft",
                                  });
                                } catch (error) {
                                  console.error("Error adding label:", error);
                                  notification.error({
                                    message: error.response?.data?.Output?.message || "Failed to add label",
                                    placement: "bottomLeft",
                                  });
                                }
                              }}
                              disabled={isApplied}
                            >
                              <span
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: label.color }}
                              ></span>
                              <span className="flex-1 truncate">{label.name}</span>
                              {isApplied && (
                                <span className="material-icons-outlined text-sm text-gray-400">check</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                }
                trigger="click"
                placement="bottomLeft"
                open={labelPopoverOpen}
                rootClassName="label-email-detail-popover"
                onOpenChange={setLabelPopoverOpen}
              >
                <button className="h-[20px] flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-gray-500 hover:bg-gray-100 hover:text-grigora-600 transition-colors border border-dashed border-gray-300 hover:border-grigora-300">
                  <span className="material-icons-outlined text-sm mr-1 leading-none">add</span>
                  Label
                </button>
              </Popover>
            </div>
          )}
        </div>

        {/* Trust Bar */}
        {showTrustBar && email && (
          <div 
            ref={trustBarRef}
            className={`bg-gray-100 border border-gray-200 px-4 py-3 flex items-center justify-between rounded-lg mb-6 transition-all duration-300 ${
              trustBarHighlighted ? 'ring-4 ring-grigora-500 ring-opacity-50 bg-grigora-50 border-grigora-300' : ''
            }`}
          >
            <div className="flex items-center gap-3">
              <span className="material-icons-outlined text-gray-600 text-xl">block</span>
              <span className="text-sm text-gray-900">
                Images are blocked to protect your privacy.
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onAllowImages}
                className="px-3 py-1.5 text-sm font-medium text-gray-900 bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 transition-colors"
              >
                Allow
              </button>
              <button
                onClick={onAlwaysAllowImages}
                className="px-3 py-1.5 text-sm font-medium text-gray-900 bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 transition-colors"
              >
                Always allow from {senderEmail || senderName || "this sender"}
              </button>
            </div>
          </div>
        )}

        {/* Grigora AI Banner */}
        {mailboxPath !== "Scheduled" && (
        <div className="mb-6 p-4 bg-grigora-50 rounded-lg border border-grigora-100 transition-all duration-300 ease-in-out">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center text-grigora-700">
              <span className="material-icons-outlined mr-2">auto_awesome</span>
              <span className="text-sm font-medium">Grigora AI</span>
            </div>
            {!summary && !summaryLoading && (
              <Button 
                size="sm" 
                variant="secondary" 
                onClick={async () => {
                  setSummaryLoading(true);
                  setSummaryError(null);
                  try {
                    // Send email data to API
                    const emailData = {
                      subject: email.subject,
                      senderName: email.senderName,
                      senderEmail: email.senderEmail,
                      from: {
                        name: email.senderName,
                        email: email.senderEmail,
                      },
                      to: email.to || [],
                      body: email.body,
                      html: email.body,
                      text: email.preview,
                      preview: email.preview,
                      timestamp: email.timestamp,
                      date: email.date || email.timestamp,
                    };
                    
                    const response = await mailClientAPI.summarizeThread(mailboxPath, emailId, emailData);
                    if (response.Output?.summary) {
                      setSummary(response.Output.summary);
                    } else {
                      setSummaryError("Failed to generate summary");
                    }
                  } catch (error) {
                    console.error("Error summarizing thread:", error);
                    setSummaryError(error.response?.data?.Output?.message || "Failed to summarize thread");
                  } finally {
                    setSummaryLoading(false);
                  }
                }} 
                disabled={summaryLoading}
              >
                Summarize Thread
              </Button>
            )}
          </div>

          {/* Loading State - Show Skeletons */}
          {summaryLoading && (
            <div className="mt-4 space-y-3">
              <Skeleton active paragraph={{ rows: 2 }} />
              <Skeleton active paragraph={{ rows: 1 }} />
              <div className="space-y-2">
                <Skeleton active paragraph={{ rows: 0 }} title={{ width: "60%" }} />
                <Skeleton active paragraph={{ rows: 0 }} title={{ width: "50%" }} />
                <Skeleton active paragraph={{ rows: 0 }} title={{ width: "55%" }} />
              </div>
            </div>
          )}

          {/* Error State */}
          {summaryError && !summaryLoading && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {summaryError}
            </div>
          )}

          {/* Summary Display */}
          {summary && !summaryLoading && (
            <div className="mt-4 space-y-4">
              {/* Main Summary */}
              {summary.summary && (
                <div>
                  <h4 className="text-sm font-semibold text-grigora-900 mb-2">Summary</h4>
                  <p className="text-sm leading-relaxed">{summary.summary}</p>
                </div>
              )}

              {/* Key Points */}
              {summary.key_points && summary.key_points.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-grigora-900 mb-2">Key Points</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    {summary.key_points.map((point, idx) => (
                      <li key={idx}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Participants */}
              {summary.participants && summary.participants.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-grigora-900 mb-2">Participants</h4>
                  <div className="space-y-1 text-sm">
                    {summary.participants.map((participant, idx) => (
                      <div key={idx}>
                        <span className="font-medium">{participant.name || participant.email}</span>
                        {participant.email && participant.name && (
                          <span className=""> &lt;{participant.email}&gt;</span>
                        )}
                        {participant.role && (
                          <span className=""> - {participant.role}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Items */}
              {summary.action_items && summary.action_items.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-grigora-900 mb-2">Action Items</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    {summary.action_items.map((item, idx) => (
                      <li key={idx}>
                        {item.item}
                        {item.assigned_to && (
                          <span className=""> - Assigned to: {item.assigned_to}</span>
                        )}
                        {item.deadline && (
                          <span className=""> - Deadline: {item.deadline}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Decisions */}
              {summary.decisions && summary.decisions.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-grigora-900 mb-2">Decisions</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    {summary.decisions.map((decision, idx) => (
                      <li key={idx}>{decision}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        )}

        {/* Email Body */}
        <EmailBodyDisplay body={email.body || email.preview || ""} imagesAllowed={imagesAllowed} onImageClick={handleImageClick} />

        {/* Attachments */}
        {email.hasAttachments && email.attachments && email.attachments.length > 0 && (
          <div className="mb-8 pt-4 border-t border-gray-100">
            <h4 className="text-sm font-medium text-gray-500 mb-3">Attachments ({email.attachments.length})</h4>
            <div className="flex flex-wrap gap-3">
              {email.attachments.map((attachment, idx) => {
                const formatFileSize = (bytes) => {
                  if (bytes === 0) return "0 Bytes";
                  const k = 1024;
                  const sizes = ["Bytes", "KB", "MB", "GB"];
                  const i = Math.floor(Math.log(bytes) / Math.log(k));
                  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
                };

                const getFileIcon = (contentType, filename) => {
                  const ext = filename?.split(".").pop()?.toLowerCase() || "";
                  if (contentType?.includes("image")) return "image";
                  if (contentType?.includes("pdf")) return "picture_as_pdf";
                  if (contentType?.includes("video")) return "videocam";
                  if (contentType?.includes("audio")) return "audiotrack";
                  if (contentType?.includes("zip") || contentType?.includes("rar") || ext === "zip" || ext === "rar") return "folder_zip";
                  if (contentType?.includes("text") || ext === "txt" || ext === "md") return "description";
                  if (ext === "doc" || ext === "docx") return "description";
                  if (ext === "xls" || ext === "xlsx") return "table_chart";
                  if (ext === "ppt" || ext === "pptx") return "slideshow";
                  return "attach_file";
                };

                const handleDownload = async () => {
                  const partId = attachment.partId;
                  
                  // Mark this attachment as downloading
                  setDownloadingAttachments(prev => new Set(prev).add(partId));
                  
                  try {
                    const response = await mailClientAPI.downloadAttachment(mailboxPath, emailId, partId);
                    
                    console.log("Download response:", response);
                    console.log("Response data type:", typeof response, response instanceof Blob);
                    
                    // response.data should be a Blob when responseType is 'blob'
                    let blob;
                    if (response instanceof Blob) {
                      blob = response;
                    } else {
                      // Fallback: create blob from array buffer or data
                      blob = new Blob([response], { type: attachment.contentType || "application/octet-stream" });
                    }

                    console.log("Blob:", blob);
                    // Console log the blob content
                    console.log("Blob content:", blob.toString());
                    
                    // Create a temporary URL for the blob
                    const url = window.URL.createObjectURL(blob);
                    
                    // Creatwhen ye a temporary anchor element and trigger download
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = attachment.filename || `attachment-${partId}`;
                    document.body.appendChild(link);
                    link.click();
                    
                    // Clean up
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                  } catch (error) {
                    console.error("Error downloading attachment:", error);
                    console.error("Error details:", error.response);
                    alert("Failed to download attachment. Please try again.");
                  } finally {
                    // Remove from downloading set
                    setDownloadingAttachments(prev => {
                      const next = new Set(prev);
                      next.delete(partId);
                      return next;
                    });
                  }
                };

                return (
                  <div
                    key={idx}
                    onClick={handleDownload}
                    className="group relative w-48 h-20 bg-gray-50 rounded-lg border border-gray-200 overflow-hidden hover:bg-gray-100 hover:border-gray-300 cursor-pointer transition-all flex items-center gap-3 px-3"
                  >
                    <div className="flex-shrink-0 w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
                      <span className="material-icons-outlined text-gray-600 text-xl">
                        {getFileIcon(attachment.contentType, attachment.filename)}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate" title={attachment.filename}>
                        {attachment.filename}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {formatFileSize(attachment.size || 0)}
                      </div>
                    </div>
                    <div className={`flex-shrink-0 transition-opacity ${
                      downloadingAttachments.has(attachment.partId) 
                        ? "opacity-100" 
                        : "opacity-0 group-hover:opacity-100"
                    }`}>
                      {downloadingAttachments.has(attachment.partId) ? (
                        <Spin indicator={<LoadingOutlined spin />} size="small" />
                      ) : (
                        <span className="material-icons-outlined text-gray-400 text-lg">download</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Generate AI Reply Button */}
        {!replyMode && mailboxPath !== "Scheduled" && (
          <div className="flex mb-10">
            <button 
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-grigora-50 text-grigora-700 font-medium text-sm hover:bg-grigora-100 transition-colors border border-grigora-100 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={async () => {
                setReplySuggestionsLoading(true);
                setReplySuggestionsError(null);
                try {
                  // Send email data to API
                  const emailData = {
                    subject: email.subject,
                    senderName: email.senderName,
                    senderEmail: email.senderEmail,
                    from: {
                      name: email.senderName,
                      email: email.senderEmail,
                    },
                    to: email.to || [],
                    body: email.body,
                    html: email.body,
                    text: email.preview,
                    preview: email.preview,
                    timestamp: email.timestamp,
                    date: email.date || email.timestamp,
                  };
                  
                  const response = await mailClientAPI.generateReply(mailboxPath, emailId, emailData);
                  if (response.Output?.replies && response.Output.replies.length > 0) {
                    // Use the first reply suggestion and automatically enter reply mode
                    const generatedReply = response.Output.replies[0].text;
                    pendingAiReplyRef.current = generatedReply;
                    setReplyMode("reply");
                  } else {
                    setReplySuggestionsError("Failed to generate reply");
                    notification.error({
                      message: "Failed to generate reply",
                      description: "Please try again.",
                      placement: "bottomLeft",
                    });
                  }
                } catch (error) {
                  console.error("Error generating reply:", error);
                  setReplySuggestionsError(error.response?.data?.Output?.message || "Failed to generate reply");
                  notification.error({
                    message: "Error generating reply",
                    description: error.response?.data?.Output?.message || "Please try again.",
                    placement: "bottomLeft",
                  });
                } finally {
                  setReplySuggestionsLoading(false);
                }
              }}
              disabled={replySuggestionsLoading}
            >
              <span className={`material-icons-outlined text-lg ${replySuggestionsLoading ? 'animate-spin' : ''}`}>
                {replySuggestionsLoading ? 'refresh' : 'auto_awesome'}
              </span>
              {replySuggestionsLoading ? 'Generating Reply...' : 'Generate AI Reply'}
            </button>
          </div>
        )}

        {/* Reply/Forward Section */}
        {replyMode ? (
          <div className="border border-gray-200 rounded-lg shadow-sm animate-fade-in mb-10 bg-white flex flex-col" style={{ minHeight: "400px" }}>
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <span className="material-icons-outlined text-gray-400">
                  {replyMode === "reply" || replyMode === "replyAll" ? "reply" : "forward"}
                </span>
                <span className="text-sm font-medium text-gray-700">
                  {replyMode === "reply"
                    ? `Replying to ${senderName || senderEmail}`
                    : replyMode === "replyAll"
                    ? `Replying to ${senderName || senderEmail} and others`
                    : "Forwarding"}
                </span>
              </div>
              <Tooltip title="Close" mouseEnterDelay={1}>
                <button
                  onClick={() => {
                    setReplyMode(null);
                    setReplyBody("");
                    setReplyToEmails([]);
                    setReplyCcEmails([]);
                    setReplyBccEmails([]);
                    setShowCcBcc(false);
                    setReplySubject("");
                    setReplyAttachments([]);
                  }}
                  className="flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full p-1 hover:bg-gray-100"
                >
                  <span className="material-icons-outlined text-sm">close</span>
                </button>
              </Tooltip>
            </div>

            {/* Fields */}
            <div className="flex flex-col flex-1 relative">
              {/* To Field */}
              <div className="px-4 py-2 border-b border-gray-100 flex items-center group transition-colors focus-within:bg-gray-50">
                <span className="text-gray-500 text-sm w-10">To</span>
                <EmailInput
                  emails={replyToEmails}
                  setEmails={setReplyToEmails}
                  placeholder="Recipients"
                />
                {!showCcBcc && (
                  <button
                    onClick={() => setShowCcBcc(true)}
                    className="text-xs text-gray-500 hover:text-gray-900 ml-2"
                  >
                    Cc / Bcc
                  </button>
                )}
              </div>

              {/* CC and BCC Fields */}
              {showCcBcc && (
                <>
                  <div className="px-4 py-2 border-b border-gray-100 flex items-center animate-fade-in focus-within:bg-gray-50">
                    <span className="text-gray-500 text-sm w-10">Cc</span>
                    <EmailInput
                      emails={replyCcEmails}
                      setEmails={setReplyCcEmails}
                      placeholder="Cc"
                      maxEmails={10}
                      showCount={true}
                    />
                  </div>
                  <div className="px-4 py-2 border-b border-gray-100 flex items-center animate-fade-in focus-within:bg-gray-50">
                    <span className="text-gray-500 text-sm w-10">Bcc</span>
                    <EmailInput
                      emails={replyBccEmails}
                      setEmails={setReplyBccEmails}
                      placeholder="Bcc"
                      maxEmails={10}
                      showCount={true}
                    />
                  </div>
                </>
              )}

              {/* Subject Field */}
              <div className="px-4 py-2 border-b border-gray-100 flex justify-between items-center focus-within:bg-gray-50">
                <input
                  type="text"
                  placeholder="Subject"
                  className="flex-1 outline-none text-sm py-1 font-medium bg-transparent placeholder-gray-500"
                  value={replySubject}
                  onChange={(e) => setReplySubject(e.target.value)}
                />
              </div>

              {/* Attachments */}
              {replyAttachments.length > 0 && (
                <div className="px-4 py-2 border-b border-gray-100 bg-gray-50 flex flex-wrap gap-2">
                  {replyAttachments.map((att) => (
                    <div
                      key={att.id}
                      className="flex items-center gap-2 bg-white border border-gray-200 rounded px-2 py-1 text-xs"
                    >
                      <span className="material-icons-outlined text-sm text-gray-500">attach_file</span>
                      <span className="text-gray-700 max-w-[150px] truncate">{att.name}</span>
                      <span className="text-gray-400">({formatFileSize(att.size)})</span>
                      <button
                        onClick={() => removeAttachment(att.id)}
                        className="text-gray-400 hover:text-red-600 ml-1"
                      >
                        <span className="material-icons-outlined text-sm">close</span>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Rich Text Editor Area */}
              <div className="flex-1 overflow-hidden relative flex flex-col">
                {isRefining && (
                  <div className="absolute inset-0 bg-white/80 z-20 flex items-center justify-center rounded-lg">
                    <div className="flex items-center gap-2 text-grigora-600 font-medium">
                      <Spin indicator={<LoadingOutlined spin />} />
                      <span>Refining...</span>
                    </div>
                  </div>
                )}
                
                <div className="flex-1 overflow-y-auto p-4 h-full flex flex-col">
                  <ReactMailQuill
                    ref={quillRef}
                    value={replyBody}
                    onChange={setReplyBody}
                    placeholder="Type your message here..."
                    modules={modules}
                    toolbarContainerId="#rich-area-text-effect-reply"
                    className="full-height-pseudo"
                  />
                </div>
                <style>{`
                  .ql-container {
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                    font-size: 14px;
                    border: none !important;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                  }
                  
                  /* Toolbar Container - Light grey rounded container */
                  #rich-area-text-effect-reply {
                    display: ${showFormattingToolbar ? 'flex' : 'none'} !important;
                    border: none !important;
                    background-color:rgb(255, 255, 255);
                    padding: 6px 10px !important;
                    flex-wrap: wrap;
                    align-items: center;
                    gap: 0;
                    box-shadow: none;
                  }
                  
                  /* Quill will render toolbar inside the container */
                  #rich-area-text-effect-reply.ql-toolbar,
                  #rich-area-text-effect-reply .ql-toolbar {
                    display: flex !important;
                    border: none !important;
                    background-color: transparent;
                    padding: 0;
                    margin: 0;
                    gap: 0;
                    width: 100%;
                    border-top: 1px solid rgb(243 244 246) !important;
                  }
                  .ql-snow .ql-picker.ql-expanded .ql-picker-options {
                    top: unset !important;
                    bottom: 100%;
                  }
                  .ql-snow.ql-toolbar button {
                    border-radius: 5px !important;
                  }
                  .ql-snow.ql-toolbar button:hover, .ql-snow.ql-toolbar button.ql-active {
                     color: #623ce7 !important;
                      background-color: rgb(243 244 246) !important;
                  }
                  .ql-formats{
                    display: flex !important;
                    flex-wrap: wrap;
                    gap: 10px;
                  }
                  
                  .ql-editor {
                    min-height: 200px;
                    padding: 12px;
                  }
                  
                  .ql-editor.ql-blank::before {
                    color: #9ca3af;
                    font-style: normal;
                  }
                `}</style>
              </div>
              <div className={`${showFormattingToolbar ? 'flex' : 'hidden'}`}>
                <div id="rich-area-text-effect-reply" className={`quill-toolbar-wrapper`}>
                  <span className="ql-formats">
                    <select className="ql-font"></select>
                    <button className="ql-bold"></button>
                    <button className="ql-italic"></button>
                    <button className="ql-underline"></button>
                    <button className="ql-color"></button>
                    <select className="ql-align"></select>
                    <button className="ql-list" value="ordered"></button>
                    <button className="ql-list" value="bullet"></button>
                    <button className="ql-blockquote"></button>
                    <button className="ql-link"></button>
                    <button className="ql-image"></button>
                  </span>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="px-4 py-3 flex items-center justify-between border-t border-gray-100 relative">
                <div className="flex items-center gap-4">
                  <Button onClick={handleSendReply} disabled={sendingReply}>
                    {sendingReply ? "Sending..." : "Send"}
                  </Button>

                  <div className="flex items-center gap-1 text-gray-500 relative">
                    {/* Formatting Toolbar Toggle Button */}
                    <button
                      className={`p-1.5 hover:bg-gray-100 rounded text-gray-600 flex items-center transform ${
                        showFormattingToolbar ? "bg-gray-100 text-grigora-600" : ""
                      }`}
                      onClick={() => setShowFormattingToolbar(!showFormattingToolbar)}
                      title="Formatting options"
                    >
                      <span className="material-icons-outlined text-[1.2rem]">text_format</span>
                    </button>

                    {/* Attach File Button */}
                    <button
                      className="p-1.5 hover:bg-gray-100 rounded text-gray-600 flex items-center transform"
                      onClick={() => fileInputRef.current?.click()}
                      title="Attach file"
                    >
                      <span className="material-icons-outlined text-[1.2rem]">attach_file</span>
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      className="hidden"
                      onChange={handleFileSelect}
                    />

                    {/* AI Tools Button */}
                    <div className="relative" ref={aiToolsRef}>
                      <button
                        className={`p-1.5 rounded flex items-center gap-1 transition-colors ${
                          aiToolsOpen ? "bg-grigora-100 text-grigora-600" : "text-gray-500 hover:bg-gray-100"
                        }`}
                        title="AI Tools"
                        onClick={() => setAiToolsOpen(!aiToolsOpen)}
                        disabled={isRefining}
                      >
                        <span className="material-icons-outlined text-[1.2rem]">auto_fix_high</span>
                      </button>

                      {/* AI Tools Dropdown */}
                      {aiToolsOpen && (
                        <div className="absolute bottom-full mb-2 left-0 bg-white rounded-lg shadow-xl border border-gray-200 w-48 z-30 flex flex-col py-1 overflow-hidden animate-fade-in">
                          <div className="text-[10px] font-bold text-gray-400 px-3 py-1.5 uppercase tracking-wider bg-gray-50">Tone</div>
                          <button
                            onClick={() => handleRewrite("tone_professional")}
                            className="px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 hover:text-grigora-600 transition-colors"
                            disabled={isRefining}
                          >
                            Professional
                          </button>
                          <button
                            onClick={() => handleRewrite("tone_polite")}
                            className="px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 hover:text-grigora-600 transition-colors"
                            disabled={isRefining}
                          >
                            Polite
                          </button>
                          <button
                            onClick={() => handleRewrite("tone_calm")}
                            className="px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 hover:text-grigora-600 transition-colors"
                            disabled={isRefining}
                          >
                            Calm
                          </button>

                          <div className="h-px bg-gray-100 my-1"></div>
                          <div className="text-[10px] font-bold text-gray-400 px-3 py-1.5 uppercase tracking-wider bg-gray-50">Length</div>
                          <button
                            onClick={() => handleRewrite("shorten")}
                            className="px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 hover:text-grigora-600 transition-colors"
                            disabled={isRefining}
                          >
                            Shorten
                          </button>
                          <button
                            onClick={() => handleRewrite("expand")}
                            className="px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 hover:text-grigora-600 transition-colors"
                            disabled={isRefining}
                          >
                            Expand
                          </button>

                          <div className="h-px bg-gray-100 my-1"></div>
                          <div className="text-[10px] font-bold text-gray-400 px-3 py-1.5 uppercase tracking-wider bg-gray-50">Style</div>
                          <button
                            onClick={() => handleRewrite("humanize")}
                            className="px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 hover:text-grigora-600 transition-colors"
                            disabled={isRefining}
                          >
                            Humanize
                          </button>
                          <button
                            onClick={() => handleRewrite("fix_grammar")}
                            className="px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 hover:text-grigora-600 transition-colors"
                            disabled={isRefining}
                          >
                            Fix Grammar
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  className="text-gray-400 hover:text-gray-600 flex items-center"
                  onClick={() => {
                    setReplyMode(null);
                    setReplyBody("");
                    setReplyToEmails([]);
                    setReplyCcEmails([]);
                    setReplyBccEmails([]);
                    setShowCcBcc(false);
                    setReplySubject("");
                    setReplyAttachments([]);
                  }}
                >
                  <span className="material-icons-outlined text-xl">delete</span>
                </button>
              </div>
            </div>
          </div>
        ) : (
          mailboxPath !== "Scheduled" && (
            <div className="flex gap-4 mb-10">
              <button
                onClick={() => setReplyMode("reply")}
                className="flex items-center px-6 py-2.5 border border-gray-300 rounded-lg text-gray-600 font-medium hover:bg-gray-50 hover:text-gray-800 transition-colors shadow-sm text-sm"
              >
                <span className="material-icons-outlined mr-2 text-[20px]">reply</span>
                Reply
              </button>
              <button
                onClick={() => setReplyMode("replyAll")}
                className="flex items-center px-6 py-2.5 border border-gray-300 rounded-lg text-gray-600 font-medium hover:bg-gray-50 hover:text-gray-800 transition-colors shadow-sm text-sm"
              >
                <span className="material-icons-outlined mr-2 text-[20px]">reply_all</span>
                Reply All
              </button>
              <button
                onClick={() => setReplyMode("forward")}
                className="flex items-center px-6 py-2.5 border border-gray-300 rounded-lg text-gray-600 font-medium hover:bg-gray-50 hover:text-gray-800 transition-colors shadow-sm text-sm"
              >
                <span className="material-icons-outlined mr-2 text-[20px]">forward</span>
                Forward
              </button>
            </div>
          )
        )}
      </div>
    </div>
  );
};

